import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import Cookies from "js-cookie";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { theme } from "../../../../App";
import Button from "@mui/material/Button";
import LoadingOverlay from "../../../core/LoadingOverlay";
import Typography from "@mui/material/Typography";
import ConfirmDialog from "../../../core/ConfirmDialog";
import MDToolTableViewer from "./MDToolTableViewer";
import DismissibleMessage from "../DismissibleMessage";
import { Provider } from "react-redux";
import {
    ClientsDropdown,
    DatasetTypesDropdown,
    DatetimeDropdown,
    GroupDropdown,
    ProjectsDropdown,
} from "../DropdownV2";
import {
    loadDataset,
    selectCurrentClient,
    selectCurrentProject,
    selectCurrentDatasetType,
    selectCurrentGroupKey,
    selectCurrentVersionDttm,
    setClient,
    setProject,
    setDatasetType,
    setGroupKey,
    setVersionDttm,
    setSelectedTable,
    selectDatasetIsLoading,
    selectCurrentDataset,
    startBulkUpload,
    startBulkTeardown,
    startRegenerateRecords,
    selectBulkUploadStatus,
    selectBulkUploadError,
    selectBulkTeardownStatus,
    selectBulkTeardownError,
    selectRegenerateRecordsStatus,
    selectRegenerateRecordsError,
    selectDatasetLoadingStatus,
    selectDatasetLoadingError,
    selectBulkUploadIsLoading,
    selectBulkTeardownIsLoading,
    selectCurrentDatasetMetadata,
    selectBulkUploadIsReady,
    selectBulkTeardownIsReady,
    selectRegenerateRecordsIsReady,
    selectTableDataIsAvailable,
    selectAvailableTables,
    selectSelectedTable,
    selectRegenerateRecordsIsLoading,
    type MdUploadToolAllowedDatasetType,
    checkConductorProperties,
    selectConductorPropertiesError,
    selectConductorPropertiesIsLoading,
} from "./store/reducers/mdUploadSlice";

import { useAppDispatch, useAppSelector } from "./store/hooks";
import { store } from "./store/store";

const MASTERDATA_RECORDS_TYPE = "masterdata-records";
const MASTERDATA_RECORDS_UPLOADED_TYPE = "masterdata-records-upload";
const ALLOWED_DATASET_TYPES = [MASTERDATA_RECORDS_TYPE, MASTERDATA_RECORDS_UPLOADED_TYPE];

function ErrorText(props: { message: string }) {
    return (
        <p
            style={{
                marginTop: theme.spacing(1),
                fontWeight: "bold",
                fontSize: "20px",
                color: "red",
            }}
        >
            {props.message}
        </p>
    );
}
function StatusText(props: { message: string }) {
    return (
        <p
            style={{
                marginTop: theme.spacing(1),
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "20px",
                color: "green",
            }}
        >
            {props.message}
        </p>
    );
}

export function MDUploadTool() {
    const dispatch = useAppDispatch();

    const selectedClient = useAppSelector(selectCurrentClient);
    const selectedProject = useAppSelector(selectCurrentProject);
    const selectedDatasetType = useAppSelector(selectCurrentDatasetType);
    const selectedGroupKey = useAppSelector(selectCurrentGroupKey);
    const selectedDatetime = useAppSelector(selectCurrentVersionDttm);

    const setSelectedClient = useCallback((client: string) => dispatch(setClient(client)), [dispatch, setClient]);
    const setSelectedProject = useCallback((project: string) => dispatch(setProject(project)), [dispatch, setProject]);
    const setSelectedDatasetType = useCallback(
        (datasetType: string) => dispatch(setDatasetType(datasetType as MdUploadToolAllowedDatasetType)),
        [dispatch, setDatasetType]
    );
    const setSelectedGroupKey = useCallback((groupKey: string) => dispatch(setGroupKey(groupKey)), [dispatch, setGroupKey]);
    const setSelectedVersionDttm = useCallback(
        (datetime: string) => dispatch(setVersionDttm(datetime)),
        [dispatch, setVersionDttm]
    );

    const selectedDataset = useAppSelector(selectCurrentDataset);
    const selectedDatasetMetadata = useAppSelector(selectCurrentDatasetMetadata);
    const datasetLoadingStatus = useAppSelector(selectDatasetLoadingStatus);
    const datasetLoadingError = useAppSelector(selectDatasetLoadingError);
    const datasetIsLoading = useAppSelector(selectDatasetIsLoading);
    const uploadStatus = useAppSelector(selectBulkUploadStatus);
    const uploadError = useAppSelector(selectBulkUploadError);
    const isUploading = useAppSelector(selectBulkUploadIsLoading);
    const teardownStatus = useAppSelector(selectBulkTeardownStatus);
    const teardownError = useAppSelector(selectBulkTeardownError);
    const isTearingDown = useAppSelector(selectBulkTeardownIsLoading);
    const regenerateRecordsStatus = useAppSelector(selectRegenerateRecordsStatus);
    const regenerateRecordsError = useAppSelector(selectRegenerateRecordsError);
    const isRegeneratingRecords = useAppSelector(selectRegenerateRecordsIsLoading);
    const tableDataIsAvailable = useAppSelector(selectTableDataIsAvailable);
    const availableTables = useAppSelector(selectAvailableTables);
    const selectedTable = useAppSelector(selectSelectedTable);

    const uploadIsReady = useAppSelector(selectBulkUploadIsReady);
    const teardownIsReady = useAppSelector(selectBulkTeardownIsReady);
    const regenerateRecordsIsReady = useAppSelector(selectRegenerateRecordsIsReady);

    const conductorPropertiesError = useAppSelector(selectConductorPropertiesError);
    const conductorPropertiesIsLoading = useAppSelector(selectConductorPropertiesIsLoading);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [statusMessage, setStatusMessage] = useState<string>();

    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmModalAction, setConfirmModalAction] = useState<"upload" | "teardown" | "regenerate">();

    // There are several checks in place to ensure the user doesn't accidentally
    // upload or teardown the same group twice. Here are all the ways:
    // 1. Cookies are set on upload/teardown that expire after 15 minutes. This
    //    is mainly to prevent that first upload from being run again if the user
    //    runs it accidentally before it can finish. The cookie is used to disable
    //    the upload/teardown buttons.
    // 2. We fail fast within the calibration platform service class on upload
    //    if the masterdata-records-upload dataset already exists for the
    //    provided group.
    // 3. We disable teardown by way of a calculated flag in the store if the
    //    teardown dataset is the completed/done type.
    // 4. The backend for MD bulk upload also checks if the
    //    masterdata-records-upload dataset exists and shows that there are records currently in MD.

    const [uploadDisabled, setUploadDisabled] = useState(false);
    const [teardownDisabled, setTeardownDisabled] = useState(false);

    const uploadStateCookieKey = useMemo(
        () => `${selectedClient}_${selectedProject}_${selectedGroupKey}`,
        [selectedClient, selectedProject, selectedGroupKey]
    );

    const updateButtonDisabledStates = useCallback(() => {
        setUploadDisabled(Cookies.get(uploadStateCookieKey) === "upload");
        setTeardownDisabled(Cookies.get(uploadStateCookieKey) === "teardown");
    }, [setUploadDisabled, setTeardownDisabled, uploadStateCookieKey]);

    useEffect(() => {
        // Attempt to use the cookie to disable the upload/teardown buttons
        // anytime the selected group changes
        updateButtonDisabledStates();
    }, [uploadStateCookieKey]);

    const metadataBasedSummary = useMemo(() => {
        if (!selectedDatasetMetadata) {
            return "";
        }
        if (selectedDatasetType === MASTERDATA_RECORDS_TYPE) {
            return `Calculated from Client Asset Data on ${
                selectedDatasetMetadata?.generated_at
            } by ${selectedDatasetMetadata?.owner_emails.join(", ")}`;
        }
        if (selectedDatasetType === MASTERDATA_RECORDS_UPLOADED_TYPE && tableDataIsAvailable) {
            return `Uploaded on ${
                selectedDatasetMetadata?.generated_at
            } by ${selectedDatasetMetadata?.owner_emails.join(", ")}`;
        } else if (selectedDatasetType === MASTERDATA_RECORDS_UPLOADED_TYPE && !tableDataIsAvailable) {
            return `Torn down on ${
                selectedDatasetMetadata?.generated_at
            } by ${selectedDatasetMetadata?.owner_emails.join(", ")}`;
        }
    }, [selectedDatasetMetadata, selectedDatasetType]);

    const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // For setting error messages to show for only 10s
    const setErrorWithTimeout = useCallback(
        (error: string, timeoutInMs: number) => {
            setErrorMessage(error);

            // Clear any existing timeout
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }

            // Clear error state after timeout
            errorTimeoutRef.current = setTimeout(() => setErrorMessage(undefined), timeoutInMs);
        },
        [setErrorMessage]
    );
    const setErrorWithDefaultTimeout = useCallback((error: string) => setErrorWithTimeout(error, 10000), [setErrorWithTimeout]);

    // For setting the status message of uploading/teardown for only 10s
    const setStatusWithTimeout = useCallback(
        (status: string, timeoutInMs: number) => {
            setStatusMessage(status);

            // Clear any existing timeout
            if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
            }

            // Clear status message state after timeout
            statusTimeoutRef.current = setTimeout(() => setStatusMessage(undefined), timeoutInMs);
        },
        [setStatusMessage]
    );

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
            if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
            }
        };
    }, []);

    const clearMessages = useCallback(() => {
        setStatusMessage(undefined);
        setErrorMessage(undefined);
    }, [setStatusMessage, setErrorMessage]);

    // Upload related functions
    const handleUpload = useCallback(async () => {
        const isComplete = Cookies.get(uploadStateCookieKey) === "upload";
        if (isComplete) {
            setErrorWithTimeout("You have already uploaded this group to Masterdata.", 10000);
            return;
        }
        dispatch(startBulkUpload());
    }, [uploadStateCookieKey, dispatch, startBulkUpload, setErrorWithTimeout]);

    useEffect(() => {
        if (uploadStatus === "succeeded" || uploadStatus === "timeout") {
            Cookies.set(uploadStateCookieKey, "upload", { expires: 1 / 96 });
            updateButtonDisabledStates();
        }
    }, [uploadStatus, uploadStateCookieKey]);

    useEffect(() => {
        if (uploadStatus === "succeeded") {
            setStatusWithTimeout("Successfully uploaded to Masterdata!", 10000);
        } else if (uploadStatus === "timeout") {
            setStatusWithTimeout("Upload is still running... Check back within 15 minutes.", 900000); // 15 minute message
        } else if (uploadStatus === "failed") {
            setErrorWithTimeout(`Failed to upload to Masterdata, ${uploadError}`, 10000);
        }
    }, [uploadStatus, uploadError, setErrorWithTimeout, setStatusWithTimeout]);

    // Teardown related functions
    const handleTeardown = useCallback(async () => {
        const isComplete = Cookies.get(uploadStateCookieKey) === "teardown";
        if (isComplete) {
            setErrorWithTimeout("You have already uploaded this group to Masterdata.", 10000);
            return;
        }
        dispatch(startBulkTeardown());
    }, [uploadStateCookieKey, dispatch, startBulkTeardown, setErrorWithTimeout]);

    useEffect(() => {
        if (teardownStatus === "succeeded" || teardownStatus === "timeout") {
            Cookies.set(uploadStateCookieKey, "teardown", { expires: 1 / 96 });
            updateButtonDisabledStates();
        }
    }, [teardownStatus, uploadStateCookieKey]);

    useEffect(() => {
        if (teardownStatus === "succeeded") {
            setStatusWithTimeout("Successfully torn down from Masterdata!", 10000);
        } else if (teardownStatus === "timeout") {
            setStatusWithTimeout("Teardown is still running... Check back within 15 minutes.", 900000); // 15 minute message
        } else if (teardownStatus === "failed") {
            setErrorWithTimeout(`Failed to teardown from Masterdata, ${teardownError}`, 10000);
        }
    }, [teardownStatus, teardownError, setErrorWithTimeout, setStatusWithTimeout]);

    // Regenerate records related functions
    const handleRegenerateRecords = useCallback(async () => {
        dispatch(startRegenerateRecords());
    }, [dispatch, startRegenerateRecords]);

    useEffect(() => {
        if (regenerateRecordsStatus === "succeeded") {
            setStatusWithTimeout("Successfully regenerated masterdata records!", 10000);
        } else if (regenerateRecordsStatus === "timeout") {
            setStatusWithTimeout("Regenerate MD Records is still running... Check back within 15 minutes.", 900000); // 15 minute message
        } else if (regenerateRecordsStatus === "failed") {
            setErrorWithTimeout(`Failed to regenerate masterdata records, ${regenerateRecordsError}`, 10000);
        }
    }, [regenerateRecordsStatus, regenerateRecordsError, setErrorWithTimeout, setStatusWithTimeout]);

    // Modal related functions
    const openUploadModal = useCallback(() => {
        setConfirmModalAction("upload");
        setConfirmModalOpen(true);
    }, [setConfirmModalAction, setConfirmModalOpen]);

    const openTeardownModal = useCallback(() => {
        setConfirmModalAction("teardown");
        setConfirmModalOpen(true);
    }, [setConfirmModalAction, setConfirmModalOpen]);

    const openRegenerateRecordsModal = useCallback(() => {
        setConfirmModalAction("regenerate");
        setConfirmModalOpen(true);
    }, [setConfirmModalAction, setConfirmModalOpen]);

    const handleModalConfirm = useCallback(() => {
        if (confirmModalAction === "upload") {
            handleUpload();
        } else if (confirmModalAction === "teardown") {
            handleTeardown();
        } else if (confirmModalAction === "regenerate") {
            handleRegenerateRecords();
        }
        setConfirmModalOpen(false);
    }, [confirmModalAction, handleUpload, handleTeardown, handleRegenerateRecords, setConfirmModalOpen]);

    // Dataset loading related functions
    useEffect(() => {
        if (datasetLoadingStatus === "failed") {
            setErrorWithTimeout(`Failed to load the dataset, ${datasetLoadingError}`, 10000);
        }
    }, [datasetLoadingStatus, datasetLoadingError]);

    // Initialize the table once all dropdown options have been selected
    useEffect(() => {
        clearMessages();

        if (selectedDatetime) {
            dispatch(loadDataset());
            if (selectedDatasetType === MASTERDATA_RECORDS_TYPE) {
                dispatch(checkConductorProperties());
            }
        }
    }, [selectedDatetime, selectedDatasetType]);

    const [conductorPropertiesErrorDismissed, setConductorPropertiesErrorDismissed] = useState(false);

    // Reset dismissed state when a new error comes in
    useEffect(() => {
        if (conductorPropertiesError) {
            setConductorPropertiesErrorDismissed(false);
        }
    }, [conductorPropertiesError]);

    return (
        <div
            id="form-container"
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                width: "90%",
                margin: "0 auto",
                padding: 3,
            }}
        >
            <h1 style={{ textAlign: "center" }}>Masterdata Upload</h1>
            <form
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    width: "100%",
                    marginBottom: 16,
                }}
            >
                <div style={{ display: "flex", gap: "16px", width: "100%" }}>
                    <ClientsDropdown
                        value={selectedClient}
                        setValue={setSelectedClient}
                        setError={setErrorWithDefaultTimeout}
                        styleClass={{ flex: 1, mx: 1 }}
                    />
                    <ProjectsDropdown
                        clientName={selectedClient}
                        value={selectedProject}
                        setValue={setSelectedProject}
                        setError={setErrorWithDefaultTimeout}
                        styleClass={{ flex: 1, mx: 1 }}
                    />
                    <DatasetTypesDropdown
                        clientName={selectedClient}
                        projectName={selectedProject}
                        value={selectedDatasetType}
                        allowedDatasetTypes={ALLOWED_DATASET_TYPES}
                        setValue={setSelectedDatasetType}
                        setError={setErrorWithDefaultTimeout}
                        styleClass={{ flex: 1, mx: 1 }}
                    />
                </div>
                <div style={{ display: "flex", gap: "16px", width: "100%" }}>
                    <GroupDropdown
                        clientName={selectedClient}
                        projectName={selectedProject}
                        datasetType={selectedDatasetType}
                        value={selectedGroupKey}
                        setValue={setSelectedGroupKey}
                        setError={setErrorWithDefaultTimeout}
                        styleClass={{ flex: 1, mx: 1 }}
                    />
                    <DatetimeDropdown
                        clientName={selectedClient}
                        projectName={selectedProject}
                        datasetType={selectedDatasetType}
                        group={selectedGroupKey}
                        value={selectedDatetime}
                        setValue={setSelectedVersionDttm}
                        setError={setErrorWithDefaultTimeout}
                        styleClass={{ flex: 1, mx: 1 }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{
                            height: "50px",
                            padding: "0 16px",
                            minWidth: "160px",
                            whiteSpace: "nowrap",
                            fontSize: "0.725rem",
                        }}
                        onClick={openRegenerateRecordsModal}
                        disabled={!regenerateRecordsIsReady}
                    >
                        Regenerate MD Records
                    </Button>
                </div>
            </form>

            <ConfirmDialog
                open={confirmModalOpen}
                handleClose={() => {
                    setConfirmModalOpen(false);
                }}
                handleConfirm={handleModalConfirm}
                title={`Confirm ${confirmModalAction}`}
                message={`Are you sure you want to ${confirmModalAction} for:`}
                newLineMessage={`${selectedClient}/${selectedProject}`}
            />

            {errorMessage && <ErrorText message={errorMessage} />}
            {statusMessage && <StatusText message={statusMessage} />}
            {conductorPropertiesError && !conductorPropertiesErrorDismissed && (
                <div style={{ textAlign: "center" }}>
                    <DismissibleMessage
                        message={`${conductorPropertiesError} Upload is disabled.`}
                        type="error"
                        onDismiss={() => setConductorPropertiesErrorDismissed(true)}
                    />
                    <p style={{ fontSize: "14px", color: "red", margin: 0 }}>
                        Check ROOT or upload the new conductor using MDUI
                    </p>
                </div>
            )}

            <LoadingOverlay isLoading={datasetIsLoading || conductorPropertiesIsLoading} message="" />
            <LoadingOverlay isLoading={isUploading} message="Uploading to Masterdata..." />
            <LoadingOverlay isLoading={isTearingDown} message="Tearing down from Masterdata..." />
            <LoadingOverlay isLoading={isRegeneratingRecords} message="Regenerating Masterdata records..." />

            {!datasetIsLoading &&
                selectedClient &&
                selectedProject &&
                selectedDatasetType &&
                selectedDatetime &&
                selectedDataset && (
                    <>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: theme.spacing(2),
                                marginTop: theme.spacing(4),
                            }}
                        >
                            <FormControl
                                variant="outlined"
                                sx={{
                                    margin: theme.spacing(1),
                                    minWidth: 120,
                                    maxWidth: 300,
                                }}
                            >
                                <InputLabel id="table-select-label">Table</InputLabel>
                                <Select
                                    labelId="table-select-label"
                                    id="table-select"
                                    value={selectedTable}
                                    onChange={(event) => {
                                        dispatch(setSelectedTable(event.target.value));
                                    }}
                                    label="Table"
                                    sx={{
                                        width: "100%",
                                    }}
                                >
                                    {availableTables.map((tableName) => (
                                        <MenuItem key={tableName} value={tableName}>
                                            {tableName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography
                                sx={{
                                    marginTop: theme.spacing(1),
                                    textAlign: "center",
                                    flexGrow: 1,
                                    fontSize: "16px",
                                }}
                                variant="h6"
                            >
                                <p>{metadataBasedSummary}</p>
                            </Typography>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    marginTop: theme.spacing(2),
                                }}
                            >
                                {selectedDatasetType === MASTERDATA_RECORDS_TYPE && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        sx={{ ml: theme.spacing(2) }}
                                        onClick={openUploadModal}
                                        disabled={uploadDisabled || !uploadIsReady}
                                    >
                                        Upload to Masterdata
                                    </Button>
                                )}
                                {selectedDatasetType === MASTERDATA_RECORDS_UPLOADED_TYPE && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        sx={{ ml: theme.spacing(2) }}
                                        onClick={openTeardownModal}
                                        disabled={teardownDisabled || !teardownIsReady}
                                    >
                                        Teardown from Masterdata
                                    </Button>
                                )}
                            </div>
                        </div>
                        <MDToolTableViewer />
                    </>
                )}
        </div>
    );
}

export default function MdUploadApp(props: any): JSX.Element {
    return (
        <Provider store={store}>
            <MDUploadTool {...props} />
        </Provider>
    );
}
